/**
 * refineQuery — matching + suggestion logic for the Refine/Filter chip bar
 * (`RefineChipBar`), shared between the Cogni results table
 * (`SearchResultsTable`'s `drug_name` column filter) and the Keyword/Instant
 * results table (`DrugResultsTable`'s `ResultsFilterBox`).
 *
 * The matching/suggestion core (`matchesRefineChips` / `suggestRefineChips`)
 * operates on a normalized `RefineFields` shape, not on either surface's raw
 * row type. Each surface supplies its own adapter (`cogniRefineFields` /
 * `drugTableRefineFields`) that maps its rows to `RefineFields` — see each
 * adapter's doc for exactly what it derives.
 *
 * Chips are built client-side from the rows currently loaded (no backend
 * call). A chip is one of:
 *   - region: a region code ('US'|'EU'|'UK'|'CA'|'AU'|'JP'|'CN'|'CH'|'DE'|'IE'|'IT'). Matched
 *     against `RefineFields.region` exactly — each adapter is responsible for
 *     normalizing a null/empty row region to 'US' before it reaches here.
 *   - year: a single year ("2019") or an inclusive range stored as a
 *     normalized "min-max" string ("2015-2020"), matched against
 *     `RefineFields.year`; `null` never matches.
 *   - brand / generic / manufacturer: case-insensitive EXACT equality with
 *     `RefineFields.brand` / `.generic` / `.manufacturer`. `null` never matches.
 *   - text: a case-insensitive substring of `RefineFields.contains` (an
 *     already-lowercased haystack the adapter builds).
 *
 * Matching (`matchesRefineChips`): chips of the same kind are ORed for
 * region/year/brand/generic/manufacturer; `text` chips are ANDed with each
 * other; different kinds are ANDed together. No chips matches everything.
 *
 * Suggestions (`suggestRefineChips`) are built from `baseFields` — the rows
 * (as `RefineFields`) passing the caller's OTHER filters (for Cogni: region
 * pills, approval-year dropdown, last-updated — computed via TanStack's
 * faceted row model on the `drug_name` column; for Keyword/Instant: simply
 * every row on the current page, since the chip bar is the only filter).
 * Each candidate's displayed `count` is how many baseFields rows would match
 * once that candidate is added to the chips already active; zero-count and
 * already-active candidates are dropped.
 */
import type { DrugResultV2 } from '../types';
import { approvalYear } from './approvalDate';
import { regionDisplayLabel } from '../constants/filterConstants';
import { brandLabel, sortableApprovalDate, type DrugTableFields } from './drugResultsTable';

export type RefineChipKind = 'region' | 'year' | 'brand' | 'generic' | 'manufacturer' | 'text';

export interface RefineChip {
  kind: RefineChipKind;
  value: string;
}

export interface RefineSuggestion {
  kind: RefineChipKind;
  value: string;
  count: number;
}

/** Normalized row shape the matching/suggestion core operates on. Each
 *  surface supplies an adapter that builds this from its own row type. */
export interface RefineFields {
  brand: string | null;
  generic: string | null;
  manufacturer: string | null;
  /** Already normalized — a null/empty row region must be resolved to 'US'
   *  by the adapter before this reaches the matching/suggestion core. */
  region: string;
  year: number | null;
  /** Already-lowercased haystack for `text` chips. */
  contains: string;
}

export const REFINE_KIND_LABELS: Record<RefineChipKind, string> = {
  region: 'Region',
  year: 'Year',
  brand: 'Brand',
  generic: 'Generic',
  manufacturer: 'Manufacturer',
  text: 'Contains',
};

// Canonical region code order — mirrors REGION_OPTIONS in
// constants/filterConstants.ts. Duplicated (rather than imported) to keep
// this a dependency-free pure module.
const REGION_CODE_ORDER = ['US', 'US_FDA', 'EU', 'UK', 'CA', 'AU', 'JP', 'CN', 'CH', 'DE', 'IE', 'IT'];

// A Map, not an object literal: an object lookup would also hit inherited
// keys like "constructor" and misclassify them as a region alias.
const REGION_ALIASES = new Map<string, string>([
  ['usa', 'US'],
  ['europe', 'EU'],
  ['japan', 'JP'],
  ['canada', 'CA'],
  ['australia', 'AU'],
  ['china', 'CN'],
  ['switzerland', 'CH'],
  ['swiss', 'CH'],
  ['germany', 'DE'],
  ['german', 'DE'],
  ['ireland', 'IE'],
  ['irish', 'IE'],
  ['italy', 'IT'],
  ['italian', 'IT'],
]);

const YEAR_FULL_RE = /^(?:19|20)\d{2}$/;
const YEAR_RANGE_RE = /^((?:19|20)\d{2})-((?:19|20)\d{2})$/;
const YEAR_PREFIX_RE = /^\d{1,3}$/;

function yearRangeFromValue(value: string): { min: number; max: number } {
  const range = YEAR_RANGE_RE.exec(value);
  if (range) {
    return { min: Number(range[1]), max: Number(range[2]) };
  }
  const year = Number(value);
  return { min: year, max: year };
}

/** Display form of a chip's value — a year range shows with an en dash, and a
 *  region value reads its display label once US-Drugs@FDA is visible. */
export function formatRefineChipValue(
  chip: Pick<RefineChip, 'kind' | 'value'>,
  usFdaVisible = false,
): string {
  if (chip.kind === 'region') return regionDisplayLabel(chip.value, usFdaVisible);
  if (chip.kind === 'year') {
    const range = YEAR_RANGE_RE.exec(chip.value);
    if (range) return `${range[1]}–${range[2]}`;
  }
  return chip.value;
}

function fieldMatchesExact(fieldValue: string | null, chipValue: string): boolean {
  if (!fieldValue) return false;
  return fieldValue.toLowerCase() === chipValue.toLowerCase();
}

/** Whether `fields` matches the Refine chip bar's active `chips`. See module doc for the contract. */
export function matchesRefineChips(fields: RefineFields, chips: RefineChip[]): boolean {
  if (chips.length === 0) return true;

  const byKind = (kind: RefineChipKind) => chips.filter((c) => c.kind === kind);

  const regionChips = byKind('region');
  if (regionChips.length > 0 && !regionChips.some((c) => c.value === fields.region)) {
    return false;
  }

  const yearChips = byKind('year');
  if (yearChips.length > 0) {
    if (fields.year === null) return false;
    const year = fields.year;
    if (!yearChips.some((c) => { const { min, max } = yearRangeFromValue(c.value); return year >= min && year <= max; })) {
      return false;
    }
  }

  const brandChips = byKind('brand');
  if (brandChips.length > 0 && !brandChips.some((c) => fieldMatchesExact(fields.brand, c.value))) {
    return false;
  }

  const genericChips = byKind('generic');
  if (genericChips.length > 0 && !genericChips.some((c) => fieldMatchesExact(fields.generic, c.value))) {
    return false;
  }

  const manufacturerChips = byKind('manufacturer');
  if (manufacturerChips.length > 0 && !manufacturerChips.some((c) => fieldMatchesExact(fields.manufacturer, c.value))) {
    return false;
  }

  const textChips = byKind('text');
  if (textChips.length > 0 && !textChips.every((c) => fields.contains.includes(c.value.toLowerCase()))) {
    return false;
  }

  return true;
}

const MAX_SUGGESTIONS_PER_KIND = 5;

function chipExists(chips: RefineChip[], candidate: RefineChip): boolean {
  return chips.some((c) => c.kind === candidate.kind && c.value.toLowerCase() === candidate.value.toLowerCase());
}

function countMatches(baseFields: RefineFields[], chips: RefineChip[]): number {
  let n = 0;
  for (const fields of baseFields) {
    if (matchesRefineChips(fields, chips)) n++;
  }
  return n;
}

function regionSuggestionValues(q: string, baseFields: RefineFields[]): string[] {
  const present = new Set<string>();
  for (const fields of baseFields) {
    present.add(fields.region);
  }
  const aliasedCodes = new Set<string>();
  for (const [alias, code] of REGION_ALIASES) {
    if (alias.startsWith(q)) aliasedCodes.add(code);
  }
  return REGION_CODE_ORDER
    .filter((code) => present.has(code) && (code.toLowerCase().startsWith(q) || aliasedCodes.has(code)));
}

function yearSuggestionValues(q: string, baseFields: RefineFields[]): string[] {
  if (YEAR_FULL_RE.test(q)) return [q];

  const range = YEAR_RANGE_RE.exec(q);
  if (range) {
    const a = Number(range[1]);
    const b = Number(range[2]);
    return [`${Math.min(a, b)}-${Math.max(a, b)}`];
  }

  if (YEAR_PREFIX_RE.test(q)) {
    const years = new Set<number>();
    for (const fields of baseFields) {
      if (fields.year !== null) years.add(fields.year);
    }
    return Array.from(years)
      .filter((year) => String(year).startsWith(q))
      .sort((a, b) => b - a)
      .map(String);
  }

  return [];
}

function fieldSuggestionValues(
  q: string,
  baseFields: RefineFields[],
  field: 'brand' | 'generic' | 'manufacturer',
): string[] {
  const counts = new Map<string, { display: string; count: number }>();
  for (const fields of baseFields) {
    const raw = fields[field];
    if (!raw) continue;
    if (!raw.toLowerCase().includes(q)) continue;
    const key = raw.toLowerCase();
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { display: raw, count: 1 });
    }
  }
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.display.localeCompare(b.display))
    .map((v) => v.display);
}

/**
 * Suggestions for the Refine chip bar's dropdown. `query` is the typed text;
 * `baseFields` are the rows (as `RefineFields`) passing every OTHER filter
 * (see module doc); `currentChips` are the chips already active, ANDed into
 * every candidate's displayed count and used to suppress already-active chips.
 */
export function suggestRefineChips(
  query: string,
  baseFields: RefineFields[],
  currentChips: RefineChip[],
): RefineSuggestion[] {
  const rawTrimmed = query.trim();
  const q = rawTrimmed.toLowerCase();
  if (!q) return [];

  // The 5-per-kind cap applies AFTER zero-count candidates are dropped, so an
  // active chip that zeroes the most frequent values can't hide the rest.
  const addGroup = (kind: RefineChipKind, values: string[]) => {
    const group: RefineSuggestion[] = [];
    for (const value of values) {
      if (group.length === MAX_SUGGESTIONS_PER_KIND) break;
      const candidate: RefineChip = { kind, value };
      if (chipExists(currentChips, candidate)) continue;
      const count = countMatches(baseFields, [...currentChips, candidate]);
      if (count > 0) group.push({ kind, value, count });
    }
    return group;
  };

  return [
    ...addGroup('region', regionSuggestionValues(q, baseFields)),
    ...addGroup('year', yearSuggestionValues(q, baseFields)),
    ...addGroup('brand', fieldSuggestionValues(q, baseFields, 'brand')),
    ...addGroup('generic', fieldSuggestionValues(q, baseFields, 'generic')),
    ...addGroup('manufacturer', fieldSuggestionValues(q, baseFields, 'manufacturer')),
    ...addGroup('text', [rawTrimmed]),
  ];
}

// ── Adapters ─────────────────────────────────────────────────────────────

/** Cogni (SearchResultsTable) adapter. `year` is derived via the shared
 *  `approvalYear` (null → null) — NEVER `new Date(...).getFullYear()`, which
 *  parses a date-only string as UTC midnight and rolls a Jan-1 value (how
 *  year-precision dates are stored) back a year west of GMT; that was this
 *  adapter's derivation until it was found to disagree with the table's
 *  displayed year for exactly those rows. Region defaulted to 'US', and the
 *  same five-field haystack for `text` chips. */
export function cogniRefineFields(
  row: DrugResultV2,
  includeApprovalDate = true,
): RefineFields {
  const year = includeApprovalDate ? approvalYear(row.approval_date) : null;
  return {
    brand: row.drug_name || null,
    generic: row.generic_name || null,
    manufacturer: row.manufacturer || null,
    region: row.region || 'US',
    year,
    contains: `${(row.drug_name || '').toLowerCase()} ${(row.generic_name || '').toLowerCase()} ${(row.manufacturer || '').toLowerCase()} ${(row.evidence_text || '').toLowerCase()} ${(row.evidence?.chunk_text || '').toLowerCase()}`,
  };
}

/** Keyword/Instant (DrugResultsTable) adapter. `year` is derived by SLICING
 *  the formatted (already-sliced) approval date string, never `new Date()`
 *  (spec §4.1 — a date-only value parsed as UTC shifts back a day in US time
 *  zones). `contains` covers exactly the five DISPLAYED values the old
 *  `filterDrugRows` haystack used — no snippets, no Matches count. */
export function drugTableRefineFields(
  row: DrugTableFields,
  includeApprovalDate = true,
): RefineFields {
  const brand = brandLabel(row);
  // The bar refines only DISPLAYED values: with the Approval Date column
  // hidden (non-US-only search) the date is neither suggested nor matchable,
  // the rule the free-text filter carried as `includeApprovalDate`.
  const dateStr = includeApprovalDate ? sortableApprovalDate(row.approval_date) : null;
  const year = dateStr ? Number(dateStr.slice(0, 4)) : NaN;
  return {
    brand,
    generic: row.generic_name || null,
    manufacturer: row.manufacturer || null,
    region: row.region,
    year: Number.isNaN(year) ? null : year,
    contains: [brand, row.generic_name, row.manufacturer, row.region, dateStr]
      .filter((v): v is string => v != null)
      .map((v) => v.toLowerCase())
      .join(' '),
  };
}

// ── URL persistence (the `refine` search param) ─────────────────────────
//
// Wire shape: a compact JSON array [{k, v}] — mirrors pages/chipUrlState.ts.
// Malformed input (bad JSON, non-array, an entry with an unknown kind,
// empty value, invalid region code or invalid year format) fails the WHOLE
// parse closed to []. A raw value that does not look like a JSON array is
// treated as a legacy free-text link (pre-chip `refine` values): one `text`
// chip per whitespace-separated word, preserving old links' meaning exactly.
// Cogni-only (Keyword/Instant chips are not persisted to the URL).

const VALID_REFINE_KINDS: ReadonlySet<string> = new Set<RefineChipKind>([
  'region',
  'year',
  'brand',
  'generic',
  'manufacturer',
  'text',
]);

interface WireRefineChip {
  k: RefineChipKind;
  v: string;
}

function isValidRegionValue(value: string): boolean {
  return (REGION_CODE_ORDER as string[]).includes(value);
}

function isValidYearValue(value: string): boolean {
  if (YEAR_FULL_RE.test(value)) return true;
  const range = YEAR_RANGE_RE.exec(value);
  if (!range) return false;
  return Number(range[1]) <= Number(range[2]);
}

/** Serialize chips into the compact JSON the `refine` URL param holds. An
 *  empty chip list serializes to '' so the caller can delete the param. */
export function serializeRefineChips(chips: RefineChip[]): string {
  if (chips.length === 0) return '';
  const wire: WireRefineChip[] = chips.map((c) => ({ k: c.kind, v: c.value }));
  return JSON.stringify(wire);
}

/** Parse the `refine` URL param back into RefineChip[]. Never throws. */
export function parseRefineParam(raw: string | null): RefineChip[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (!trimmed.startsWith('[')) {
    // Legacy free-text link.
    return trimmed.split(/\s+/).filter(Boolean).map((word) => ({ kind: 'text' as const, value: word }));
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const result: RefineChip[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') return [];
    const kind = (entry as Record<string, unknown>)['k'];
    const value = (entry as Record<string, unknown>)['v'];
    if (typeof kind !== 'string' || !VALID_REFINE_KINDS.has(kind)) return [];
    if (typeof value !== 'string' || value.length === 0) return [];
    if (kind === 'region' && !isValidRegionValue(value)) return [];
    if (kind === 'year' && !isValidYearValue(value)) return [];
    result.push({ kind: kind as RefineChipKind, value });
  }
  return result;
}
